import React from 'react';
import { motion } from 'framer-motion';
import { Crown, Target, TrendingUp, Users, Shield, Compass, Award, Zap } from 'lucide-react';
export function LeadershipPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-831">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-832">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-833">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-834">
            <span className="w-8 h-px bg-teal" data-id="element-835"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-836">
              Applications
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-837"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-838">
            Leadership Development and Readiness
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-839">
            SyNeural's platform architecture is designed to help organizations
            and institutions build stronger leadership by focusing on the
            capabilities that matter most when responsibility increases.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-840">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-841">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-842">
            Through CJI and NCJI, the platform provides a structured framework
            for understanding judgment, identifying readiness gaps, and
            connecting those gaps to meaningful pathways for growth,
            development, and higher responsibility over time.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-843">
            Leadership is often evaluated through incomplete signals such as
            charisma, seniority, credentials, confidence, or visibility. But as
            responsibility rises, those signals become less reliable on their
            own. The real demands of leadership involve reasoning under
            pressure, consequence awareness, emotional stability, communication,
            systems thinking, responsibility at scale, and the ability to make
            decisions without destabilizing the institution or environment being
            led. SyNeural helps make those capabilities more visible and more
            actionable.
          </p>
        </motion.div>
      </section>

      {/* Application Areas */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto space-y-20" data-id="element-844">
        {/* Leadership Readiness */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-845">
          <div className="flex items-center gap-4 mb-6" data-id="element-846">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-847">
              <Target size={28} data-id="element-848" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-849">
              Leadership Readiness
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-850">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-851">
              One of SyNeural's core strengths is its ability to help
              distinguish between status and readiness. Leadership readiness is
              not just about ambition or presentation. It is about whether an
              individual can absorb complexity, think through tradeoffs,
              regulate emotion under pressure, communicate clearly, and carry
              broader consequences responsibly. The platform helps create a more
              structured way to assess and develop that readiness over time.
            </p>
          </div>
        </motion.div>

        {/* From Potential to Responsibility */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-852">
          <div className="flex items-center gap-4 mb-6" data-id="element-853">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-854">
              <TrendingUp size={28} data-id="element-855" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-856">
              From Potential to Responsibility
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-857">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-858">
              Many individuals show promise long before they are fully ready for
              greater responsibility. SyNeural helps make that transition more
              intentional by identifying strengths, bottlenecks, and
              developmental priorities that influence whether potential can
              mature into reliable leadership. This makes the platform useful
              not only for evaluating who is ready now, but for developing who
              could become ready with the right support and structure.
            </p>
          </div>
        </motion.div>

        {/* Decision-Making Under Pressure */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-859">
          <div className="flex items-center gap-4 mb-6" data-id="element-860">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-861">
              <Zap size={28} data-id="element-862" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-863">
              Decision-Making Under Pressure
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-864">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-865">
              As leadership stakes rise, decision-making under pressure becomes
              increasingly important. SyNeural can support development in areas
              such as emotional regulation, consequence reasoning, systems
              thinking, communication in high-stress settings, and the ability
              to maintain stability amid complexity and conflict. This makes the
              platform especially useful in environments where leadership
              failures can create broad institutional, civic, or organizational
              consequences.
            </p>
          </div>
        </motion.div>

        {/* Leadership Development Pathways */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-866">
          <div className="flex items-center gap-4 mb-6" data-id="element-867">
            <div className="w-14 h-14 rounded-xl bg-navy-light flex items-center justify-center text-white shadow-lg" data-id="element-868">
              <Compass size={28} data-id="element-869" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-870">
              Leadership Development Pathways
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-871">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-872">
              A core principle of the SyNeural platform is that every meaningful
              weakness should be paired with a pathway. In leadership contexts,
              this means developmental gaps should not simply disqualify a
              person from future growth. They should also help define what
              stronger readiness would require and how it can be built. SyNeural
              supports this by translating insight into practical development
              pathways tied to coaching, training, mentorship, structured
              experience, and institutional support.
            </p>
          </div>
        </motion.div>

        {/* Institutional Leadership */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-873">
          <div className="flex items-center gap-4 mb-6" data-id="element-874">
            <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center text-white shadow-lg" data-id="element-875">
              <Shield size={28} data-id="element-876" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-877">
              Institutional Leadership and Responsibility
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-878">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-879">
              Leadership inside institutions requires more than decisiveness. It
              requires the ability to manage complexity, absorb dissent, align
              action with long-term goals, and act with responsibility toward
              the larger system rather than personal ego alone. SyNeural helps
              organizations better understand and develop these qualities in
              people moving into management, executive, civic, or public-facing
              leadership roles.
            </p>
          </div>
        </motion.div>

        {/* Leadership Pipelines */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-880">
          <div className="flex items-center gap-4 mb-6" data-id="element-881">
            <div className="w-14 h-14 rounded-xl bg-teal flex items-center justify-center text-white shadow-lg" data-id="element-882">
              <Users size={28} data-id="element-883" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-884">
              Leadership Pipelines and Succession
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-885">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-886">
              Organizations and institutions often struggle with succession
              because they lack a clear framework for identifying who is
              developing into leadership readiness and what development still
              needs to happen. SyNeural helps make leadership pipelines more
              structured by showing where readiness is strong, where it is still
              forming, and how targeted support can improve long-term leadership
              depth. This makes it relevant for succession planning, advancement
              pathways, and institutional continuity.
            </p>
          </div>
        </motion.div>

        {/* Executive and High-Responsibility */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-887">
          <div className="flex items-center gap-4 mb-6" data-id="element-888">
            <div className="w-14 h-14 rounded-xl bg-green-soft flex items-center justify-center text-white shadow-lg" data-id="element-889">
              <Crown size={28} data-id="element-890" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-891">
              Executive and High-Responsibility Environments
            </h2>
          </div>
          <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-892">
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-893">
              At higher levels of leadership, poor judgment can create
              large-scale consequences. SyNeural is designed to become more
              rigorous as the stakes of decision-making rise. In executive,
              senior institutional, and public-sector environments, the platform
              becomes especially relevant for evaluating consequence reasoning,
              crisis stability, communication under pressure, governance
              readiness, and broader responsibility at scale. This allows
              leadership development to become more disciplined and less
              dependent on impression management alone.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Leadership Capabilities */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-894">
        <div className="max-w-5xl mx-auto" data-id="element-895">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-896">
            <h2 className="text-3xl font-heading font-bold text-ink-dark mb-12 text-center" data-id="element-897">
              Key Leadership Capabilities
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-898">
              {[{
              icon: <Target size={20} data-id="element-899" />,
              label: 'Reasoning Under Pressure'
            }, {
              icon: <Compass size={20} data-id="element-900" />,
              label: 'Consequence Awareness'
            }, {
              icon: <Shield size={20} data-id="element-901" />,
              label: 'Emotional Stability'
            }, {
              icon: <Users size={20} data-id="element-902" />,
              label: 'Communication Clarity'
            }, {
              icon: <Award size={20} data-id="element-903" />,
              label: 'Systems Thinking'
            }, {
              icon: <Crown size={20} data-id="element-904" />,
              label: 'Responsibility at Scale'
            }].map((item, index) => <motion.div key={item.label} initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} className="bg-white p-6 rounded-lg border border-line" data-id="element-905">
                  <div className="w-10 h-10 rounded-md bg-navy/10 flex items-center justify-center text-navy mb-3" data-id="element-906">
                    {item.icon}
                  </div>
                  <div className="text-sm font-medium text-ink-dark" data-id="element-907">
                    {item.label}
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-908">
        <div className="max-w-4xl mx-auto" data-id="element-909">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-910">
            <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-911">
              Why It Matters for Leadership Development
            </h2>
            <p className="text-xl leading-relaxed opacity-90" data-id="element-912">
              Leadership is one of the most consequential forms of human
              capability because leadership judgment scales through
              institutions, organizations, and communities. SyNeural helps
              create a more structured and developmental way to understand that
              capability. It supports the long-term goal of building leaders who
              are not simply visible or ambitious, but genuinely prepared to
              carry complexity, responsibility, and consequence with greater
              competence over time.
            </p>
          </motion.div>
        </div>
      </section>
    </div>;
}